import { Injectable, Signal, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { InMemoryCacheService, UtilsService } from 'src/app/common';
import { KeywordSearchOptions } from 'src/app/pages/seo/models/keyword-search-options';
import { KeywordsForSite, RelatedKeywordRequest, KeywordSuggestionsRequest, KeywordIdeasRequest, RankedKeywordsRequest } from '../models/keyword-search-request';
import { SeoApiService } from '../seo-api.service';

@Injectable({
  providedIn: 'root'
})
export class KeywordSearchService {

  private _cachedSearchResults: any;
  private _loadMore = signal<boolean>(false);


  constructor(private utils: UtilsService, private translate: TranslateService, private cache: InMemoryCacheService) { 
    this.resetCachedSearchResults();
  }

  public resetCachedSearchResults(): void {
    this._cachedSearchResults = {
      relatedKeywords: null,
      keywordSuggestions: null,
      keywordIdeas: null,
    };
  }

  public loadMoreResults(): void {
    this.utils.logger.info('KeywordSearchService.loadMoreResults()');
    this._loadMore.update(value => !value);
  }

  public serializeRelatedKeywords(options: KeywordSearchOptions, filters: string = '', searchResultsLength: number): RelatedKeywordRequest {
    if (!options.keyword) {
      throw new Error('No keyword provided');
    }

    this.utils.logger.info(`KeywordSearchService.serializeRelatedKeywords() - keyword = ${options.keyword} and location = ${options.locationCode} and language = ${options.languageCode}.`);

    const data: RelatedKeywordRequest =  {
          "keyword": options.keyword,
          "languageCode": options.languageCode,
          "locationCode": options.locationCode,
          "depth": this.cache.seoLimits.relatedKeywordsDepth,
          "includeSeedKeyword": true,
          "includeSerpInfo": true,
          "includeSubdomains": true,
          "filtersString": filters,
          "orderByString": null,
          "limit": this.cache?.seoLimits?.keywordServiceRelatedKeywords,
          "offset": options.loadMore ? searchResultsLength : 0,
          "replace_with_core_keyword": false
    }
    
    return data;
  }

  public serializeKeywordSuggestions(options: KeywordSearchOptions, filters: string = '', searchResultsLength: number): KeywordSuggestionsRequest {

    if (!options.keyword) {
      throw new Error('No keyword provided');
    }

    this.utils.logger.info(`KeywordSearchService.serializeKeywordSuggestions() - keyword = ${options.keyword} and location = ${options.locationCode}`);

    const data: KeywordSuggestionsRequest = {
      "keyword": options.keyword,
      "locationCode": options.locationCode,
      "languageCode": options.languageCode,
      "includeSerpInfo": true,
      "includeSubdomains": true,
      "includeSeedKeyword": true,
      "filtersString": filters,
      "orderByString": null,
      "limit": this.cache?.seoLimits?.keywordServiceKeywordSuggestions,
      "offset": options.loadMore ? searchResultsLength : 0,
      "exactMatch": true
   }
    
    return data;
  }

  public serializeKeywordsForSite(options: KeywordSearchOptions, filters: string = ''): KeywordsForSite {

    if (!options.keyword) {
      throw new Error('No keyword provided');
    }

    this.utils.logger.info(`KeywordSearchService.serializeDomainSearch() - keyword = ${options.keyword} and location = ${options.locationCode} and language = ${options.languageCode}.`);

    const data: RankedKeywordsRequest = {
      "target": options.keyword,
      "languageCode": options.languageCode,
      "locationCode": options.locationCode,
      "filtersString": filters,
      "orderByString": null,
      "limit": this.cache?.seoLimits?.keywordServiceKeywordsForSite,
      "offset": 0
    }
    
    return data;
  }

  public serializeKeywordIdeas(options: KeywordSearchOptions, filters: string = '', searchResultsLength: number): KeywordIdeasRequest {

    if (!options.keyword) {
      throw new Error('No keyword provided');
    }

    this.utils.logger.info(`KeywordSearchService.serializeKeywordIdeas() - keyword = ${options.keyword} and location = ${options.locationCode}`);

    const data: KeywordIdeasRequest = {
      "keywords": [options.keyword],
      "locationCode": options.locationCode,
      "languageCode": options.languageCode,
      "includeSerpInfo": true,
      "includeSubdomains": true,
      "closelyVariants": true,
      "filtersString": filters,
      "orderByString": null,
      "limit": this.cache?.seoLimits?.keywordServiceKeywordIdeas,
      "offset": options.loadMore ? searchResultsLength : 0
   }
    
    return data;
  }

  public setCachedSearchResults(type: string, data: any): void {
    this._cachedSearchResults[type] = data;
  }

  public filterSearchVolumeByDateRange(data, selectedRange) {
    const currentDate = new Date();
    let cutoffYear = currentDate.getFullYear();
    let cutoffMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed, so add 1 for comparison
    let monthsToSubtract;
  
    switch (selectedRange) {
      case 'last_6_months':
        monthsToSubtract = 6;
        break;
      case 'last_12_months':
        monthsToSubtract = 12;
        break;
      case 'last_24_months':
        monthsToSubtract = 24;
        break;
      case 'all_time':
        return data; // Return all data for 'all_time'
      default:
        return []; // Return empty array if no valid range is selected
    }
  
    for (let i = 0; i < monthsToSubtract; i++) {
      if (cutoffMonth === 1) {
        cutoffYear--;
        cutoffMonth = 12;
      } else {
        cutoffMonth--;
      }
    }
  
    return data.filter(item => {
      return (item.year > cutoffYear) || (item.year === cutoffYear && item.month >= cutoffMonth);
    });
  } 

  get barChartOptions(): any {
    return {
      plugins: {
          legend: {
              labels: {
                  color: '#ffffff'
              }
          }
      },
      scales: {
          x: {
              ticks: {
                  color: '#ffffff'
              },
              grid: {
                  //color: '#ebedef'
              }
          },
          y: {
              ticks: {
                  color: '#ffffff'
              },
              grid: {
                  //color: '#ebedef'
              }
          }
      }
    }
  }

  get cachedSearchResults(): any {
    return this._cachedSearchResults;
  }

  get loadMore(): Signal<boolean> {
    return this._loadMore;
  }
}
